import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiMoon, FiSun } from "react-icons/fi";
import { useLanguage } from "../../i18n/LanguageContext";
import { useTheme } from "../../theme/ThemeContext";
import { useScrolled } from "../../hooks/useScrolled";
import { useActiveSection } from "../../hooks/useActiveSection";
import { smoothScrollToId, smoothScrollToTop } from "../../utils/smoothScroll";
import { Icon } from "../common/Icon";
import "./Navbar.css";

// Certifications section is temporarily hidden — see App.tsx.
const SECTION_IDS = ["about", "skills", "projects", "experience", "education", "support", "contact"];

export function Navbar() {
  const { t, locale, setLocale } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const scrolled = useScrolled(20);
  const active = useActiveSection(SECTION_IDS);
  const [menuOpen, setMenuOpen] = useState(false);

  const links = SECTION_IDS.map((id) => ({
    id,
    label: t.nav[id as keyof typeof t.nav],
  }));

  const handleNavClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    smoothScrollToId(id);
  };

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    smoothScrollToTop();
  };

  return (
    <motion.header
      className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="navbar__inner container">
        <a href="#about" className="navbar__logo" onClick={scrollToTop} data-cursor="hover">
          <span className="navbar__logo-mark" />
          Murilo<span className="gradient-text">Marques</span>
        </a>

        <nav className="navbar__links" aria-label="Main navigation">
          {links.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`navbar__link ${active === link.id ? "navbar__link--active" : ""}`}
              onClick={handleNavClick(link.id)}
              data-cursor="hover"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="navbar__actions">
          <div className="lang-switch" role="group" aria-label="Language selector">
            <button
              className={`lang-switch__option ${locale === "pt" ? "lang-switch__option--active" : ""}`}
              onClick={() => setLocale("pt")}
              data-cursor="hover"
            >
              PT
            </button>
            <span className="lang-switch__divider">/</span>
            <button
              className={`lang-switch__option ${locale === "en" ? "lang-switch__option--active" : ""}`}
              onClick={() => setLocale("en")}
              data-cursor="hover"
            >
              EN
            </button>
          </div>

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            data-cursor="hover"
          >
            <Icon icon={theme === "dark" ? FiSun : FiMoon} />
          </button>

          <button
            className="navbar__burger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? t.nav.menuClose : t.nav.menuOpen}
            aria-expanded={menuOpen}
            data-cursor="hover"
          >
            <span className={`navbar__burger-line ${menuOpen ? "navbar__burger-line--top" : ""}`} />
            <span className={`navbar__burger-line ${menuOpen ? "navbar__burger-line--hidden" : ""}`} />
            <span className={`navbar__burger-line ${menuOpen ? "navbar__burger-line--bottom" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <nav className="navbar__mobile-links">
              {links.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className={`navbar__mobile-link ${active === link.id ? "navbar__mobile-link--active" : ""}`}
                  onClick={handleNavClick(link.id)}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
